import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, RefreshCw, Database, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/admin/backups/list`);
      setBackups(response.data.backups);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  const createBackup = async () => {
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      
      const response = await axios.post(`${API}/admin/backups/create`);
      
      setSuccess('Backup created successfully!');
      
      // Refresh the list
      await fetchBackups();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = (filename) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${API}/admin/backups/download/${filename}`;
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.target = '_blank';
    
    // Add authorization header via fetch
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess(`Downloading ${filename}...`);
      setTimeout(() => setSuccess(null), 3000);
    })
    .catch(err => {
      setError('Download failed. Please try again.');
    });
  };

  const restoreBackup = async (filename) => {
    if (!window.confirm('⚠️ WARNING: This will OVERWRITE all current data!\n\nA safety backup will be created first.\n\nAre you sure you want to continue?')) {
      return;
    }

    if (!window.confirm('⚠️ FINAL CONFIRMATION\n\nType YES in the next prompt to proceed with restore.')) {
      return;
    }

    const confirmation = window.prompt('Type YES (in capital letters) to confirm restore:');
    if (confirmation !== 'YES') {
      setError('Restore cancelled. You must type YES to confirm.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      setRestoring(true);
      setError(null);
      setSuccess(null);
      
      const response = await axios.post(`${API}/admin/backups/restore/${filename}`);
      
      setSuccess(
        `✅ Database restored successfully!\n\nRestored from: ${response.data.restored_from}\n` +
        `Safety backup created: ${response.data.safety_backup}\n\n` +
        `Collections restored: ${response.data.collections_restored.length}\n` +
        `Please refresh the page to see restored data.`
      );
      
      // Refresh the backups list
      await fetchBackups();
      
      // Prompt user to refresh
      setTimeout(() => {
        if (window.confirm('Database restored! Click OK to refresh the page.')) {
          window.location.reload();
        }
      }, 3000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
      setRestoreConfirm(null);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Database size={32} color="#dc2626" />
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', margin: 0 }}>
              Database Backups
            </h1>
          </div>
          <button
            onClick={createBackup}
            disabled={creating}
            style={{
              padding: '12px 24px',
              background: creating ? '#9ca3af' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: creating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => !creating && (e.currentTarget.style.background = '#b91c1c')}
            onMouseLeave={(e) => !creating && (e.currentTarget.style.background = '#dc2626')}
          >
            <RefreshCw size={18} className={creating ? 'spin' : ''} />
            {creating ? 'Creating...' : 'Create New Backup'}
          </button>
        </div>
        <p style={{ color: '#6b7280', fontSize: '1rem' }}>
          Manage your database backups. Download them to your computer for safekeeping.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '16px',
          background: '#fef2f2',
          border: '2px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertCircle size={24} color="#dc2626" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>Error</div>
            <div style={{ color: '#7f1d1d' }}>{error}</div>
          </div>
        </div>
      )}

      {success && (
        <div style={{
          padding: '16px',
          background: '#f0fdf4',
          border: '2px solid #bbf7d0',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <CheckCircle size={24} color="#16a34a" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: '600', color: '#14532d', marginBottom: '4px' }}>Success</div>
            <div style={{ color: '#15803d' }}>{success}</div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div style={{
        padding: '20px',
        background: '#fef3c7',
        border: '2px solid #fbbf24',
        borderRadius: '8px',
        marginBottom: '2rem'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#78350f', marginBottom: '8px' }}>
          💡 Important: Save Backups to a Safe Location
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e', fontSize: '0.95rem', lineHeight: '1.6' }}>
          <li>Download backups regularly (daily during launch week)</li>
          <li>Store them on your computer, Google Drive, or Dropbox</li>
          <li>Keep at least the last 7 days of backups</li>
          <li>These backups are your safety net if anything goes wrong</li>
        </ul>
      </div>

      {/* Backups List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <RefreshCw size={32} color="#dc2626" className="spin" />
          <p style={{ color: '#6b7280', marginTop: '1rem' }}>Loading backups...</p>
        </div>
      ) : backups.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem',
          background: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db'
        }}>
          <Database size={48} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            No backups yet
          </h3>
          <p style={{ color: '#6b7280' }}>Click "Create New Backup" to create your first backup</p>
        </div>
      ) : (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0 }}>
              Available Backups ({backups.length})
            </h3>
            <button
              onClick={fetchBackups}
              style={{
                padding: '8px 16px',
                background: '#f3f4f6',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.9rem'
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          <div style={{ display: 'grid', gap: '16px' }}>
            {backups.map((backup, index) => (
              <div
                key={backup.filename}
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#dc2626';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Database size={24} color="#dc2626" />
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                      {backup.filename}
                    </h4>
                    {index === 0 && (
                      <span style={{
                        padding: '4px 12px',
                        background: '#dcfce7',
                        color: '#15803d',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        LATEST
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '0.9rem', color: '#6b7280' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={16} />
                      {formatDate(backup.created_at)}
                    </div>
                    <div>
                      Size: <strong>{backup.size_mb} MB</strong>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => downloadBackup(backup.filename)}
                    style={{
                      padding: '10px 20px',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                  >
                    <Download size={18} />
                    Download
                  </button>

                  <button
                    onClick={() => restoreBackup(backup.filename)}
                    disabled={restoring}
                    style={{
                      padding: '10px 20px',
                      background: restoring ? '#9ca3af' : '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: restoring ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => !restoring && (e.currentTarget.style.background = '#d97706')}
                    onMouseLeave={(e) => !restoring && (e.currentTarget.style.background = '#f59e0b')}
                  >
                    <RefreshCw size={18} />
                    {restoring ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default BackupManager;
